import { DefinitionDataSourceType } from "@wingspanhq/payments/dist/interfaces/requirement";
import type { insuranceMonitorSettingsApiSpec } from "@wingspanhq/wingspan-api-types";
import * as Yup from "yup";

import { RequirementDefinitionGeneralLiabilityFormValues } from "../../../../types";
import { getBaseEditValues } from "../../../../utils/getBaseEditValues";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import {
  FormPartialGeneralLiability,
  GENERAL_LIABILITY_AGGREGATE_MINIMUM_COMMON,
  GENERAL_LIABILITY_PER_OCCURRENCE_MINIMUM_COMMON
} from "../FormPartialGeneralLiability";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
*/

export const GENERAL_LIABILITY_FORM: RequirementDefinitionTemplateForm<
  insuranceMonitorSettingsApiSpec.InsuranceCoverageMonitorSettings,
  RequirementDefinitionGeneralLiabilityFormValues,
  insuranceMonitorSettingsApiSpec.CreateInsuranceCoverageMonitorSettingsRequest
> = {
  getDefaultSettingsValues: template => ({
    acceptWingspanMasterPolicy: true,
    coverageType: "occurrence",
    name: template.name,
    perIncidentLimitMinimum: `${GENERAL_LIABILITY_PER_OCCURRENCE_MINIMUM_COMMON}`,
    totalCoverageLimitMinimum: `${GENERAL_LIABILITY_AGGREGATE_MINIMUM_COMMON}`
  }),
  getEditSettingsValues(template, requirementDefinition, settings) {
    const coverage = settings.coverages.find(
      c => c.policyType === "GeneralLiability"
    );
    const perIncident = coverage?.limits?.find(
      l => l.limitType === "PerAccident"
    );
    const aggregate = coverage?.limits?.find(
      l => l.limitType === "PolicyAggregate"
    );
    const acceptWingspan =
      coverage?.acceptedSourceTypes?.includes("PlanEnrollment") ?? false;
    const coverageType =
      coverage?.coverageForm === "ClaimsMade" ? "claimsMade" : "occurrence";

    return {
      acceptWingspanMasterPolicy: acceptWingspan,
      coverageType,
      perIncidentLimitMinimum: String(perIncident?.amount ?? 0),
      totalCoverageLimitMinimum: String(aggregate?.amount ?? 0)
    };
  },
  mapDataSourceResponseToRequirementCreateRequest(response) {
    return {
      dataSourceId: response.insuranceCoverageMonitorSettingsId,
      dataSourceType: DefinitionDataSourceType.InsuranceCoverageMonitorSettings
    };
  },
  mapFormDataToDataSourceCreateRequest(values) {
    const {
      acceptWingspanMasterPolicy,
      totalCoverageLimitMinimum,
      perIncidentLimitMinimum,
      coverageType,
      ...other
    } = values;

    return {
      ...other,
      coverages: [
        {
          acceptedSourceTypes: acceptWingspanMasterPolicy
            ? ["InsuranceImport", "PlanEnrollment"]
            : ["InsuranceImport"],
          appliedProvisions: [],
          coverageForm:
            coverageType === "claimsMade" ? "ClaimsMade" : "Occurrence",
          limits: [
            {
              amount: Number(perIncidentLimitMinimum),
              currency: "USD",
              limitType: "PerAccident"
            },
            {
              amount: Number(totalCoverageLimitMinimum),
              currency: "USD",
              limitType: "PolicyAggregate"
            }
          ],
          policyType: "GeneralLiability"
        }
      ]
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's PL/GL Master Policy" />
      <FormPartialGeneralLiability />
    </>
  ),
  successCreationMessage: "Requirement created successfully",
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    coverageType: Yup.string()
      .oneOf(["occurrence", "claimsMade"])
      .required("Required") as Yup.AnySchema,
    perIncidentLimitMinimum: Yup.string().required("Required"),
    totalCoverageLimitMinimum: Yup.string().required("Required")
  })
};
